import { useState, type FormEvent } from 'react';
import { Button, IconButton, LinkButton } from './primitives/Button.js';
import { FREE_AUDIT } from '../data/plans.js';
import { SIGNUP_URL } from '../config.js';
import { useTurnstile } from '@dedupr/turnstile';
import './AuditRequestModal.css';

/** Which call-to-action opened the form. Mirrors worker/audit-request.ts. */
export type AuditInterest = 'audit' | 'sandbox';

export interface AuditRequestModalProps {
  open: boolean;
  /** Defaults to the free audit; `sandbox` asks for the local-first build. */
  interest?: AuditInterest;
  onClose: () => void;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const RECIPIENT_FALLBACK_EMAIL = 'hello@dedupr.cloud';

/**
 * One form, two intents. The fields are identical, so a second modal would be a
 * second copy of the Turnstile handling and the mailto fallback — the copy is
 * what differs, and it has to differ: a form headed "Run a free data audit" is
 * the wrong thing to show someone who clicked "Get the local sandbox".
 *
 * ⚠️ **The subtitles are READ from `data/plans.ts`, not typed here (2026-08-10).** This
 * file hardcoded "Two systems · 50k records · one findings report" while the pricing card
 * beside it hardcoded the same sentence and a duration — and when the audit's window was
 * settled at seven days, one copy moved and the other did not. Since this modal is now the
 * audit's ONLY surface (the pricing card is gone), a stale sentence here has nothing left
 * to contradict it and would simply stand.
 */
const COPY: Record<AuditInterest, { title: string; subtitle: string; submit: string; success: string }> = {
  audit: {
    title: 'Run a free data audit',
    subtitle: FREE_AUDIT.scope,
    submit: 'Send request',
    /* ⚠️ Says both halves. "We'll be in touch" alone left somebody waiting for us when they could
       start in the next minute; only offering the button would throw away the lead.

       ⚠️ **"right now" used to mean "in this tab", and that stopped being true on 2026-08-06.**
       Local mode is the installed app — `App.tsx` derives the tier from `isInstalled()` — and Z-9
       then removed the ungated entry from the front door, so the button below opens sign-up. The
       "do not wait for us" half is still true and is the reason this sentence exists; what it
       promised on the way there was not. Wording follows `GatewaySignIn`'s front door. */
    success:
      "we have your request. You do not have to wait for us, though — start a free trial with the button below and run the audit on your own machine. There is no card and nothing to cancel.",
  },
  sandbox: {
    title: 'Get the local sandbox',
    subtitle: 'Runs in your browser · your records never leave your device',
    /* The sandbox subtitle stays a literal on purpose: `PWA.whatItDoes` is a list of full
       sentences written for a section, not a one-line chip, so reading it here would mean
       paraphrasing it — and a paraphrase is the second copy the note above warns about. */
    submit: 'Send it over',
    /* ⚠️ **The first clause was right and the second was not.** "The sandbox is the installed app"
       is exactly the 2026-08-06 decision; "go and get it now" then described a download a visitor
       could reach cold, which Z-9 closed — the front door offers nothing until it knows who is
       asking. So this said the correct fact and then contradicted it in the same sentence. */
    success:
      "we have your request. The sandbox is the installed app — start a free trial with the button below and install it from there, rather than waiting for us. There is no card and nothing to cancel.",
  },
};

export function AuditRequestModal({ open, interest = 'audit', onClose }: AuditRequestModalProps) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [systems, setSystems] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const turnstile = useTurnstile();

  if (!open) return null;

  function reset() {
    setEmail('');
    setCompany('');
    setSystems('');
    setStatus('idle');
    setError('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      const response = await fetch('/api/audit-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company, systems, interest, turnstileToken: turnstile.token }),
      });
      const data = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      // Turnstile tokens are single-use — reset regardless of outcome so a
      // retry (or the next form open) gets a fresh one rather than a
      // guaranteed-stale token that fails verification again.
      turnstile.reset();
      if (!response.ok || !data?.ok) {
        setError(data?.error || 'Could not send your request right now. Please try again shortly.');
        setStatus('error');
        return;
      }
      setStatus('success');
    } catch {
      turnstile.reset();
      setError('Could not reach the server. Check your connection and try again.');
      setStatus('error');
    }
  }

  return (
    <div className="land-audit-overlay" role="dialog" aria-modal="true" aria-label={COPY[interest].title}>
      <div className="land-audit">
        <div className="land-audit__header">
          <div>
            <div className="land-audit__title">{COPY[interest].title}</div>
            <div className="land-audit__subtitle">{COPY[interest].subtitle}</div>
          </div>
          <IconButton size={32} aria-label="Close" onClick={handleClose}>
            ✕
          </IconButton>
        </div>

        <div className="land-audit__content">
          {status === 'success' ? (
            <div className="land-audit__success">
              <div className="land-audit__success-glyph">✓</div>
              <div className="land-audit__success-title">Request sent</div>
              <p className="land-audit__success-body">
                <strong>{email}</strong> — {COPY[interest].success}
              </p>
              {/* ⚠️ **This was a dead end.** The form emailed us and created nothing — no account,
                  no trial, no audit — so the entire path from "run a free data audit" to actually
                  running one was a human reading an inbox. Meanwhile the product's own gate already
                  asks for exactly these three things AND creates something: an organisation, a
                  7-day trial, the on-device tier. The request still reaches us, so nobody is lost
                  if they stop here; the difference is that carrying on is now possible. */}
              <LinkButton variant="primary" size="lg" href={SIGNUP_URL}>
                Start it now
              </LinkButton>
              <Button variant="secondary" size="lg" onClick={handleClose}>
                Not now
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="land-audit__form">
              {/* ⚠️ **The window and the read-only promise are stated here, and were stated
                  nowhere (2026-08-10).** The removed pricing card carried "for 30 days" — the
                  wrong figure — and this form, the audit's actual point of commitment, said
                  nothing about how long it lasts or that it changes nothing. Taking the card
                  away without adding this would have removed a wrong answer and left no
                  answer, which is worse for the one person who asked.

                  Both facts read from `FREE_AUDIT`, and the "nothing is deleted" half is not
                  optional: a time limit stated on its own reads as a threat to the visitor's
                  own data, when what actually happens is that findings stay readable and only
                  new work stops. */}
              {interest === 'audit' && (
                <p className="land-audit__terms">
                  Free for {FREE_AUDIT.days} days from sign-up, and read-only throughout — we show
                  you what we found and change nothing. When it ends, everything already found
                  stays readable and downloadable; only new work needs a plan.
                </p>
              )}
              <label className="land-audit__field">
                <span>Work email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  disabled={status === 'submitting'}
                />
              </label>
              <label className="land-audit__field">
                <span>Company (optional)</span>
                <input
                  type="text"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  placeholder="Northgate Ltd"
                  disabled={status === 'submitting'}
                />
              </label>
              <label className="land-audit__field">
                <span>Systems you use (optional)</span>
                <input
                  type="text"
                  value={systems}
                  onChange={(event) => setSystems(event.target.value)}
                  placeholder="Salesforce, HubSpot, a pile of CSVs…"
                  disabled={status === 'submitting'}
                />
              </label>

              {status === 'error' && (
                <div className="land-audit__error">
                  {error}{' '}
                  <a href={`mailto:${RECIPIENT_FALLBACK_EMAIL}`}>Email us directly instead ↗</a>
                </div>
              )}

              <div ref={turnstile.containerRef} className="land-audit__turnstile" />
              {turnstile.error && <div className="land-audit__error">{turnstile.error}</div>}

              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={status === 'submitting' || (turnstile.enabled && !turnstile.token)}
              >
                {status === 'submitting'
                  ? 'Sending…'
                  : turnstile.enabled && !turnstile.token
                    ? 'Verifying…'
                    : COPY[interest].submit}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
